import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import crmApi, { money } from '../../utils/crmApi';
import useLiveRefresh from '../../utils/useLiveRefresh';

const round3 = n => Math.round((Number(n) || 0) * 1000) / 1000;

export default function CrmStock() {
  const { isAdmin } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recon, setRecon] = useState(null);
  const navigate = useNavigate();

  function load() { crmApi.get('/products').then(setProducts).finally(() => setLoading(false)); }
  useEffect(() => { load(); }, []);
  useLiveRefresh(load);

  async function reconcile() {
    setRecon('working');
    try { const r = await crmApi.post('/stock/reconcile'); setRecon(`Done — updated ${r.committed} document(s); returned ${r.returned} ended service(s).`); load(); }
    catch (e) { setRecon(e?.error || 'Could not reconcile'); }
  }

  const tracked = products.filter(p => p.track_stock);
  const totalUnits = round3(tracked.reduce((s, p) => s + Number(p.stock_qty || 0), 0));
  // Group stock value by currency (cost + sale)
  const byCurrency = {};
  tracked.forEach(p => {
    const c = p.currency || 'OMR';
    byCurrency[c] = byCurrency[c] || { cost: 0, sale: 0 };
    byCurrency[c].cost += Number(p.stock_qty || 0) * Number(p.cost_price || 0);
    byCurrency[c].sale += Number(p.stock_qty || 0) * Number(p.unit_price || 0);
  });


  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '20px', fontWeight: '800' }}>Stock</h1>
          <p style={{ fontSize: '13px', color: 'var(--muted)' }}>Live stock levels. Quantities go down when a service order is approved, and recurring capacity returns to stock when its service ends.</p>
        </div>
        {isAdmin && <button className="btn btn-sm" onClick={reconcile} disabled={recon === 'working'}>{recon === 'working' ? 'Reconciling…' : 'Reconcile stock'}</button>}
      </div>
      {recon && recon !== 'working' && <div style={{ marginBottom: '14px', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 600, background: 'rgba(13,148,136,0.1)', color: '#0D9488' }}>{recon}</div>}

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div className="card" style={{ padding: '16px 18px', minWidth: '160px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Tracked Products</div>
          <div style={{ fontSize: '24px', fontWeight: '800', marginTop: '4px' }}>{tracked.length}</div>
        </div>
        <div className="card" style={{ padding: '16px 18px', minWidth: '160px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Total Units in Stock</div>
          <div style={{ fontSize: '24px', fontWeight: '800', marginTop: '4px' }}>{totalUnits}</div>
        </div>
        {Object.keys(byCurrency).map(c => (
          <React.Fragment key={c}>
            <div className="card" style={{ padding: '16px 18px', minWidth: '170px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Stock Value ({c})</div>
              <div style={{ fontSize: '18px', fontWeight: '800', marginTop: '4px', color: '#0D9488' }}>{money(byCurrency[c].sale, c)}</div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>at selling price</div>
            </div>
            <div className="card" style={{ padding: '16px 18px', minWidth: '170px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Cost Value ({c})</div>
              <div style={{ fontSize: '18px', fontWeight: '800', marginTop: '4px', color: '#D97706' }}>{money(byCurrency[c].cost, c)}</div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>what stock cost us</div>
            </div>
            <div className="card" style={{ padding: '16px 18px', minWidth: '170px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Expected Profit ({c})</div>
              <div style={{ fontSize: '18px', fontWeight: '800', marginTop: '4px', color: '#16A34A' }}>{money(byCurrency[c].sale - byCurrency[c].cost, c)}</div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>if all stock sells (sale − cost)</div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Stock table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1fr 1fr', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
          {['Product', 'Category', 'In Stock', 'Unit Cost', 'Stock Value (cost)', 'Stock Value (sale)'].map(h => (
            <div key={h} style={{ padding: '10px 16px', fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px' }}>{h}</div>
          ))}
        </div>
        {loading ? <div style={{ padding: '20px', color: 'var(--muted)' }}>Loading…</div> :
          tracked.length === 0 ? <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>No stock-tracked products yet.</div> :
            tracked.map((p, i) => {
              const qty = Number(p.stock_qty || 0);
              const low = qty <= 0;
              return (
                <div key={p.id} onClick={() => navigate('/crm/inventory')}
                  style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1fr 1fr', borderBottom: i < tracked.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'center', cursor: 'pointer' }}>
                  <div style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700' }}>{p.name}</div>
                    <div style={{ fontSize: '11px', color: '#0D9488', fontWeight: '600', fontFamily: 'var(--font-mono)' }}>{p.code || ''}</div>
                  </div>
                  <div style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--muted2)' }}>{p.category_name || '—'}</div>
                  <div style={{ padding: '12px 16px', fontSize: '15px', fontWeight: '800', color: low ? '#DC2626' : 'var(--text)' }}>{qty} <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 400 }}>{p.unit}</span></div>
                  <div style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--muted2)' }}>{money(p.cost_price, p.currency)}</div>
                  <div style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600' }}>{money(qty * Number(p.cost_price || 0), p.currency)}</div>
                  <div style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '700', color: '#0D9488' }}>{money(qty * Number(p.unit_price || 0), p.currency)}</div>
                </div>
              );
            })}
      </div>
    </div>
  );
}
